import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/project/components/site-shell";
import { ShieldCheck, HeartHandshake, Eye, FileBadge2 } from "lucide-react";
import { values, initiatives, foundationMission, foundationVision, leaders, foundationTimeline, daanAmrutLogo } from "@/project/data/sample";

const logoUrl = daanAmrutLogo;


export const Route = createFileRoute("/about")({
  head: () => ({ meta: [
    { title: "About — Daan Amrut Foundation" },
    { name: "description", content: "Daan Amrut Foundation brings together associations, communities, sanghs, clubs, animal shelters and non-profits with people who want to help." },
    { property: "og:title", content: "About — Daan Amrut Foundation" },
    { property: "og:description", content: "Our mission, vision and board — Daan Amrut Foundation." },
  ]}),
  component: About,
});

const pillars = [
  { icon: ShieldCheck, title: "Verification first", body: "Every applicant and every fund is verified before money moves." },
  { icon: Eye, title: "Track every rupee", body: "Real-time impact updates, transparent fund usage and access to annual reports — full visibility, always." },
  { icon: FileBadge2, title: "Instant 80G receipts", body: "The moment you donate, your 80G tax-exempt receipt is delivered to your inbox and phone." },
  { icon: HeartHandshake, title: "Donor-first", body: "Choose your cause, follow its journey and see how your gift changes lives." },
];

function About() {
  return (
    <SiteShell>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10" style={{ background: "var(--gradient-cream)" }} />
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <div className="inline-flex items-center gap-3 rounded-full border border-[var(--maroon)]/15 bg-white/90 py-1 pl-1 pr-3 text-xs font-medium text-[var(--maroon-deep)] shadow-[var(--shadow-soft)]">
            <img src={logoUrl} alt="" className="h-6 w-auto object-contain" />
            Registered organisation · 80G & 12A certified
          </div>
          <h1 className="mt-5 max-w-3xl font-display text-3xl font-semibold leading-tight text-[var(--maroon-deep)] sm:text-5xl lg:text-6xl">
            About Daan Amrut Foundation
          </h1>
          <p className="mt-5 max-w-2xl text-base text-foreground/75 sm:text-lg">
            At Daan Amrut Foundation, we bring together associations, communities, sanghs, clubs, animal shelters
            and other non-profits with people who want to help.
          </p>
        </div>
      </section>

      {foundationTimeline.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-4 pt-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">Our journey</p>
          <h2 className="mt-2 font-display text-3xl font-semibold text-[var(--maroon-deep)] sm:text-4xl">Milestones since 2019</h2>
          <ol className="mt-8 relative border-l-2 border-[var(--saffron)]/30 pl-6 sm:pl-8">
            {foundationTimeline.map((t) => (
              <li key={t.year} className="relative mb-8 last:mb-0">
                <span className="absolute -left-[33px] sm:-left-[41px] top-1 grid h-6 w-6 place-items-center rounded-full bg-[var(--maroon)] text-[10px] font-bold text-[var(--cream)] shadow-[var(--shadow-soft)]">•</span>
                <p className="font-display text-sm font-bold uppercase tracking-wider text-[var(--saffron-deep)]">{t.year}</p>
                <h3 className="mt-1 font-display text-lg font-semibold text-[var(--maroon-deep)] sm:text-xl">{t.title}</h3>
                <p className="mt-1 text-sm text-foreground/70">{t.body}</p>
              </li>
            ))}
          </ol>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="grid gap-8 md:grid-cols-2">
          <div className="rounded-3xl border border-border/60 bg-card p-8 shadow-[var(--shadow-soft)]">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">Our mission</p>
            <p className="mt-3 text-foreground/80">{foundationMission.statement}</p>
            <ul className="mt-5 space-y-2 text-sm text-foreground/75">
              {foundationMission.pillars.map((p) => (
                <li key={p} className="flex gap-2"><span className="text-[var(--saffron-deep)]">•</span> {p}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-border/60 bg-card p-8 shadow-[var(--shadow-soft)]">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">Our vision</p>
            <p className="mt-3 text-foreground/80">{foundationVision.statement}</p>
            <ul className="mt-5 space-y-2 text-sm text-foreground/75">
              {foundationVision.pillars.map((p) => (
                <li key={p} className="flex gap-2"><span className="text-[var(--saffron-deep)]">•</span> {p}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">How we work</p>
        <h2 className="mt-2 font-display text-3xl font-semibold text-[var(--maroon-deep)] sm:text-4xl">Trusted. Transparent. Tax-saving.</h2>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          {pillars.map((p) => (
            <div key={p.title} className="rounded-2xl border border-border/60 bg-card p-7 shadow-[var(--shadow-soft)]">
              <div className="grid h-11 w-11 place-items-center rounded-xl bg-[var(--cream-2)] text-[var(--maroon)]"><p.icon className="h-5 w-5" /></div>
              <h3 className="mt-4 font-display text-xl font-semibold">{p.title}</h3>
              <p className="mt-2 text-foreground/70">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[var(--cream-2)] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">Why Daan Amrut</p>
          <h2 className="mt-2 font-display text-3xl font-semibold text-[var(--maroon-deep)]">Why thousands trust Daan Amrut</h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {values.map((v, idx) => (
              <div key={v.title} className="rounded-2xl bg-white p-6 shadow-[var(--shadow-soft)]">
                <p className="font-display text-3xl font-semibold text-[var(--saffron-deep)]">0{idx + 1}</p>
                <h3 className="mt-3 font-display text-lg font-semibold">{v.title}</h3>
                <p className="mt-1.5 text-sm text-foreground/70">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">Board & Advisory</p>
        <h2 className="mt-2 font-display text-3xl font-semibold text-[var(--maroon-deep)]">{leaders.length} members guiding the foundation</h2>
        <p className="mt-2 text-foreground/70">Meet the full board and advisory council on the leadership page.</p>
        <Link to="/leadership" className="mt-6 inline-flex rounded-full bg-[var(--maroon)] px-5 py-2 text-sm font-semibold text-[var(--cream)]">See leadership</Link>
      </section>

      <section className="bg-[var(--cream-2)] py-16">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 md:grid-cols-2">
          <div>
            <h2 className="font-display text-3xl font-semibold text-[var(--maroon-deep)]">Our verification process</h2>
            <ol className="mt-5 space-y-3 text-foreground/80">
              <li><strong>1.</strong> Institution submits registration, PAN, bank and address documents.</li>
              <li><strong>2.</strong> 80G and 12A certificates checked for tax-benefit eligibility.</li>
              <li><strong>3.</strong> Admin team performs phone verification with the contact person.</li>
              <li><strong>4.</strong> Profile is approved; institution can start creating causes.</li>
              <li><strong>5.</strong> Each cause is reviewed before going public.</li>
            </ol>
          </div>
          <div>
            <h2 className="font-display text-3xl font-semibold text-[var(--maroon-deep)]">How donations are tracked</h2>
            <p className="mt-5 text-foreground/80">
              Every donation is attached to a specific cause. Institutions post updates with photos, expense amounts
              and proof documents. Donors receive notifications and can download a full PDF trail any time from
              their dashboard.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/initiatives" className="rounded-full bg-[var(--maroon)] px-5 py-2 text-sm font-semibold text-[var(--cream)]">Explore initiatives ({initiatives.length})</Link>
              <Link to="/leadership" className="rounded-full border border-border bg-white px-5 py-2 text-sm font-semibold text-[var(--maroon)]">Meet our leadership</Link>
            </div>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
